import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ZAxis,
  Cell,
  ReferenceLine
} from 'recharts';
import { Theaters, TrendingUp } from '@mui/icons-material';
import { brandColors } from '../../../../theme/theme';

/**
 * TheaterOpportunityMatrix Component
 * 
 * 2x2 matrix showing Aware Theater vs Outstanding Theater
 * Bubble chart with demographics as bubbles
 * Size = total audience, color = conversion rate
 */
const TheaterOpportunityMatrix = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No data available for theater opportunity analysis
        </Typography>
      </Paper>
    );
  }

  // Calculate overall totals
  const totals = data.reduce((acc, record) => ({
    totalResponses: acc.totalResponses + (record.TotalResponses || 0),
    totalAware: acc.totalAware + (record.TotalAware || 0),
    totalTheaterAmongAware: acc.totalTheaterAmongAware + (record.TotalTheaterAmongAware || 0),
    totalOutstandingTheater: acc.totalOutstandingTheater + (record.TotalOutstandingTheater || 0)
  }), {
    totalResponses: 0,
    totalAware: 0,
    totalTheaterAmongAware: 0,
    totalOutstandingTheater: 0
  });

  // Calculate percentages
  const awareTheaterPct = totals.totalAware > 0 ? (totals.totalTheaterAmongAware / totals.totalAware) * 100 : 0;
  const outstandingTheaterPct = totals.totalResponses > 0 ? (totals.totalOutstandingTheater / totals.totalResponses) * 100 : 0;

  // Build demographic segments for bubble chart
  const segments = data.map(record => {
    const ethnicity = record.ethnicity || 'Unknown';
    const age = record.agegroup || 'Unknown';
    const gender = record.gender || 'Unknown';
    
    const aware = record.TotalAware || 0;
    const responses = record.TotalResponses || 0;
    const awareTheater = record.TotalTheaterAmongAware || 0;
    const outstandingTheater = record.TotalOutstandingTheater || 0;
    
    return {
      name: `${ethnicity} / ${age} / ${gender}`,
      ethnicity,
      age,
      gender,
      x: aware > 0 ? (awareTheater / aware) * 100 : 0, // % of aware with theater intent
      y: responses > 0 ? (outstandingTheater / responses) * 100 : 0, // % outstanding with theater intent
      size: responses, // Bubble size = total responses
      awareTheater,
      outstandingTheater,
      totalTheater: awareTheater + outstandingTheater
    };
  }).filter(s => s.size > 0);

  // Color segments by ethnicity
  const ethnicityColors = {
    'White': '#2196f3',
    'Black': '#9c27b0',
    'Hispanic': '#ff9800',
    'Asian/Pacific Islander': '#4caf50',
    'Other': '#757575'
  };

  const getSegmentColor = (ethnicity) => {
    return ethnicityColors[ethnicity] || brandColors.primary;
  };

  // Find quadrants (using median values as thresholds)
  const medianX = segments.reduce((sum, s) => sum + s.x, 0) / segments.length;
  const medianY = segments.reduce((sum, s) => sum + s.y, 0) / segments.length;

  // Classify segments into quadrants
  const quadrants = {
    topRight: segments.filter(s => s.x >= medianX && s.y >= medianY),
    topLeft: segments.filter(s => s.x < medianX && s.y >= medianY),
    bottomRight: segments.filter(s => s.x >= medianX && s.y < medianY),
    bottomLeft: segments.filter(s => s.x < medianX && s.y < medianY)
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 2, maxWidth: 250 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {data.name}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Aware Theater:</strong> {data.x.toFixed(1)}%
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Outstanding Theater:</strong> {data.y.toFixed(1)}%
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Total Theater Intent:</strong> {data.totalTheater}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Sample Size: {data.size}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Theater Intent Opportunity Matrix
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Compare aware vs outstanding theater intent across demographic segments
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: `2px solid ${brandColors.primary}40`, backgroundColor: `${brandColors.primary}05` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Theaters sx={{ color: brandColors.primary, fontSize: 28, mr: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Theater Among Aware
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: brandColors.primary, mb: 1 }}>
                {awareTheaterPct.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totals.totalTheaterAmongAware.toLocaleString()} of {totals.totalAware.toLocaleString()} aware
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: `2px solid ${brandColors.secondary}40`, backgroundColor: `${brandColors.secondary}05` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ color: brandColors.secondary, fontSize: 28, mr: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Outstanding Theater
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: brandColors.secondary, mb: 1 }}>
                {outstandingTheaterPct.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totals.totalOutstandingTheater.toLocaleString()} not yet aware with theater intent
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bubble Chart Matrix */}
      <Box sx={{ mb: 3 }}>
        <ResponsiveContainer width="100%" height={500}>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number" 
              dataKey="x" 
              name="Aware Theater %" 
              domain={[0, 100]}
              label={{ 
                value: 'Theater Among Aware (%)', 
                position: 'bottom',
                offset: 40,
                style: { fontSize: 14, fontWeight: 600 }
              }}
            />
            <YAxis 
              type="number" 
              dataKey="y" 
              name="Outstanding Theater %" 
              domain={[0, 100]}
              label={{ 
                value: 'Outstanding Theater (%)', 
                angle: -90, 
                position: 'left',
                offset: 40,
                style: { fontSize: 14, fontWeight: 600 }
              }}
            />
            <ZAxis type="number" dataKey="size" range={[100, 1000]} />
            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
            
            {/* Reference lines at median */}
            <ReferenceLine 
              x={medianX} 
              stroke={brandColors.primary} 
              strokeDasharray="5 5" 
              strokeWidth={2}
            />
            <ReferenceLine 
              y={medianY} 
              stroke={brandColors.secondary} 
              strokeDasharray="5 5" 
              strokeWidth={2}
            />
            
            <Scatter name="Demographic Segments" data={segments}>
              {segments.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getSegmentColor(entry.ethnicity)}
                  opacity={0.7}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </Box>

      {/* Quadrant Analysis */}
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Quadrant Analysis
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card sx={{ border: `1px solid ${brandColors.success}40`, backgroundColor: `${brandColors.success}05` }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: brandColors.success }}>
                  High Aware & High Outstanding
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {quadrants.topRight.length} segments with strong theater intent across both groups
                </Typography>
                <Typography variant="caption">
                  {quadrants.topRight.map(s => s.ethnicity).slice(0, 3).join(', ')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ border: `1px solid ${brandColors.info}40`, backgroundColor: `${brandColors.info}05` }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: brandColors.info }}>
                  Low Aware & High Outstanding
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {quadrants.topLeft.length} segments with untapped theater potential
                </Typography>
                <Typography variant="caption">
                  {quadrants.topLeft.map(s => s.ethnicity).slice(0, 3).join(', ')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ border: `1px solid ${brandColors.warning}40`, backgroundColor: `${brandColors.warning}05` }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: brandColors.warning }}>
                  High Aware & Low Outstanding
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {quadrants.bottomRight.length} segments with strong aware base but limited expansion
                </Typography>
                <Typography variant="caption">
                  {quadrants.bottomRight.map(s => s.ethnicity).slice(0, 3).join(', ')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ border: `1px solid #75757540`, backgroundColor: '#75757505' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#757575' }}>
                  Low Aware & Low Outstanding
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {quadrants.bottomLeft.length} segments with limited theater appeal
                </Typography>
                <Typography variant="caption">
                  {quadrants.bottomLeft.map(s => s.ethnicity).slice(0, 3).join(', ')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Legend */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Ethnicity Color Legend
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {Object.entries(ethnicityColors).map(([ethnicity, color]) => (
            <Box key={ethnicity} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color }} />
              <Typography variant="caption">{ethnicity}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Bubble size represents total sample size for each demographic segment
        </Typography>
      </Box>
    </Paper>
  );
};

export default TheaterOpportunityMatrix;

